/**
 * useFallbackConfig - Hook para gerenciar configuração de fallback
 *
 * Gerencia o percentil usado para estimativas conservadoras de audiência
 * quando dados reais não estão disponíveis.
 */
import { useState, useEffect, useCallback } from 'react';
import { getFallbackConfig, updateFallbackConfig, recalculateFallback } from '../../services/api';

export const useFallbackConfig = () => {
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [percentile, setPercentile] = useState(10);

    /**
     * Carrega configuração de fallback do servidor
     */
    const loadConfig = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getFallbackConfig();
            const data = response.data.data;
            setConfig(data);
            setPercentile(data.percentile);
            return { success: true, data };
        } catch (error) {
            console.error('Erro ao carregar config de fallback:', error);
            return { success: false, error: error.message };
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Atualiza percentil no servidor
     */
    const updatePercentile = useCallback(async () => {
        try {
            setLoading(true);
            const response = await updateFallbackConfig({ percentile });
            const data = response.data.data;
            setConfig(data);
            setDialogOpen(false);
            return {
                success: true,
                data,
                message: response.data.message
            };
        } catch (error) {
            return {
                success: false,
                error: 'Erro ao atualizar percentil: ' + error.message
            };
        } finally {
            setLoading(false);
        }
    }, [percentile]);

    /**
     * Recalcula valores de fallback baseado no percentil atual
     */
    const recalculate = useCallback(async () => {
        try {
            setLoading(true);
            const response = await recalculateFallback();
            const data = response.data.data;
            setConfig(data);
            return {
                success: true,
                data,
                message: response.data.message
            };
        } catch (error) {
            return {
                success: false,
                error: 'Erro ao recalcular: ' + error.message
            };
        } finally {
            setLoading(false);
        }
    }, []);

    // Carrega configuração ao montar
    useEffect(() => {
        loadConfig();
    }, [loadConfig]);

    return {
        // Estados
        config,
        loading,
        dialogOpen,
        percentile,

        // Setters
        setDialogOpen,
        setPercentile,

        // Ações
        loadConfig,
        updatePercentile,
        recalculate
    };
};
